import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { ArrowRight, Globe, ShieldCheck, Users, Briefcase, Calendar, CheckCircle, Plane } from 'lucide-react';

export const HeroSection = () => {
    return (
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
            <div className="container mx-auto px-4 relative z-10">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    {/* Text Content */}
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, ease: 'easeOut' }}
                        className="text-left"
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="inline-flex items-center space-x-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full px-4 py-2 mb-6 backdrop-blur-sm"
                        >
                            <span className="relative flex h-3 w-3">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-cyan-500"></span>
                            </span>
                            <span className="text-cyan-300 font-medium text-sm">
                                AI-Powered Recruitment
                            </span>
                        </motion.div>

                        <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight tracking-tight">
                            Step Into <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 filter drop-shadow-[0_0_10px_rgba(6,182,212,0.5)]">
                                Your Future
                            </span>
                        </h1>

                        <p className="text-xl text-slate-300 mb-8 max-w-lg leading-relaxed">
                            We help talent move beyond borders through expert guidance, powered by AI.
                            Connecting skilled professionals with trusted employers worldwide.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4">
                            <Link to="/signup">
                                <Button
                                    size="lg"
                                    variant="primary"
                                    rightIcon={<ArrowRight className="w-5 h-5" />}
                                >
                                    Start Journey
                                </Button>
                            </Link>
                        </div>

                        <div className="mt-12 flex items-center gap-8 text-slate-400">
                            <div className="flex items-center gap-2">
                                <ShieldCheck className="w-5 h-5 text-cyan-500" />
                                <span className="text-sm font-medium">Verified Partners</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Users className="w-5 h-5 text-cyan-500" />
                                <span className="text-sm font-medium">700+ Placed</span>
                            </div>
                        </div>
                    </motion.div>

                    {/* 3D Floating Elements */}
                    <div className="relative h-[500px] hidden lg:block perspective-1000">
                        <motion.div
                            animate={{
                                y: [0, -20, 0],
                                rotateZ: [0, 2, 0],
                            }}
                            transition={{
                                duration: 6,
                                repeat: Infinity,
                                ease: 'easeInOut',
                            }}
                            className="absolute top-10 right-10 z-20"
                        >
                            <div className="bg-slate-800/80 backdrop-blur-xl p-6 rounded-2xl border border-cyan-500/30 shadow-[0_0_30px_rgba(6,182,212,0.2)] max-w-xs transform rotate-y-12 preserve-3d">
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-white font-bold text-xl shadow-lg">
                                        RN
                                    </div>
                                    <div>
                                        <h3 className="text-white font-bold">Registered Nurse</h3>
                                        <p className="text-cyan-400 text-sm">Berlin, Germany</p>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <div className="h-2 bg-slate-700 rounded-full w-full overflow-hidden">
                                        <motion.div
                                            initial={{ width: 0 }}
                                            animate={{ width: '85%' }}
                                            transition={{ duration: 1.5, delay: 0.5 }}
                                            className="h-full bg-cyan-500"
                                        />
                                    </div>
                                    <div className="flex justify-between text-xs text-slate-400">
                                        <span>Match Score</span>
                                        <span className="text-cyan-400 font-bold">98%</span>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            animate={{
                                y: [0, 30, 0],
                                rotateZ: [0, -3, 0],
                            }}
                            transition={{
                                duration: 7,
                                repeat: Infinity,
                                ease: 'easeInOut',
                                delay: 1,
                            }}
                            className="absolute bottom-20 left-10 z-10"
                        >
                            <div className="bg-slate-800/80 backdrop-blur-xl p-6 rounded-2xl border border-blue-500/30 shadow-[0_0_30px_rgba(59,130,246,0.2)] max-w-xs transform -rotate-y-12 preserve-3d">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-slate-400 text-sm">Visa Status</span>
                                    <span className="bg-green-500/20 text-green-400 px-2 py-1 rounded text-xs font-bold border border-green-500/30">
                                        APPROVED
                                    </span>
                                </div>
                                <h3 className="text-white font-bold text-lg mb-1">
                                    Work Permit
                                </h3>
                                <p className="text-slate-400 text-sm">Processing complete</p>
                            </div>
                        </motion.div>

                        {/* Candidate shortlisted! */}
                        <motion.div
                            animate={{
                                y: [0, -15, 0],
                                x: [0, 10, 0],
                                rotateZ: [0, 1, 0],
                            }}
                            transition={{
                                duration: 5.5,
                                repeat: Infinity,
                                ease: 'easeInOut',
                                delay: 0.5,
                            }}
                            className="absolute top-48 -left-4 z-20"
                        >
                            <div className="bg-slate-800/90 backdrop-blur-xl px-4 py-3 rounded-xl border border-purple-500/30 shadow-[0_0_20px_rgba(168,85,247,0.2)] flex items-center gap-3 transform rotate-y-6 preserve-3d">
                                <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400">
                                    <Briefcase className="w-4 h-4" />
                                </div>
                                <span className="text-white font-medium text-sm">Candidate shortlisted!</span>
                            </div>
                        </motion.div>

                        {/* Interview scheduled */}
                        <motion.div
                            animate={{
                                y: [0, 20, 0],
                                x: [0, -5, 0],
                                rotateZ: [0, -2, 0],
                            }}
                            transition={{
                                duration: 6.5,
                                repeat: Infinity,
                                ease: 'easeInOut',
                                delay: 1.5,
                            }}
                            className="absolute bottom-64 right-5 z-10"
                        >
                            <div className="bg-slate-800/90 backdrop-blur-xl px-4 py-3 rounded-xl border border-amber-500/30 shadow-[0_0_20px_rgba(245,158,11,0.2)] flex items-center gap-3 transform -rotate-y-6 preserve-3d">
                                <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400">
                                    <Calendar className="w-4 h-4" />
                                </div>
                                <div>
                                    <span className="block text-white font-medium text-sm">Interview scheduled</span>
                                    <span className="block text-slate-400 text-xs">Tomorrow, 10:00 AM</span>
                                </div>
                            </div>
                        </motion.div>

                        {/* Migration status - COMPLETED */}
                        <motion.div
                            animate={{
                                y: [0, -25, 0],
                                rotateZ: [0, 3, 0],
                            }}
                            transition={{
                                duration: 8,
                                repeat: Infinity,
                                ease: 'easeInOut',
                                delay: 0.2,
                            }}
                            className="absolute top-12 -left-16 lg:-left-24 z-30"
                        >
                            <div className="bg-emerald-900/40 backdrop-blur-xl px-4 py-3 rounded-xl border border-emerald-500/30 shadow-[0_0_20px_rgba(16,185,129,0.2)] flex items-center gap-3 transform rotate-y-3 preserve-3d">
                                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400">
                                    <CheckCircle className="w-4 h-4" />
                                </div>
                                <span className="text-white font-medium text-sm">Migration status - COMPLETED</span>
                            </div>
                        </motion.div>

                        {/* Journey Begins */}
                        <motion.div
                            animate={{
                                y: [0, 15, 0],
                                x: [0, 15, 0],
                                rotateZ: [0, -1, 0],
                            }}
                            transition={{
                                duration: 7.5,
                                repeat: Infinity,
                                ease: 'easeInOut',
                                delay: 2.5,
                            }}
                            className="absolute bottom-10 right-1/4 z-20"
                        >
                            <div className="bg-slate-800/90 backdrop-blur-xl px-4 py-3 rounded-xl border border-cyan-500/30 shadow-[0_0_20px_rgba(6,182,212,0.2)] flex items-center gap-3 transform rotate-y-6 preserve-3d">
                                <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400">
                                    <Plane className="w-4 h-4" />
                                </div>
                                <span className="text-cyan-300 font-bold text-sm">Journey Begins ✈️</span>
                            </div>
                        </motion.div>

                        {/* Abstract geometric shapes in background */}
                        <div className="absolute inset-0 pointer-events-none">
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-3xl animate-pulse-glow" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Scroll Indicator */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, y: [0, 10, 0] }}
                transition={{ delay: 2, duration: 2, repeat: Infinity }}
                className="absolute bottom-10 left-1/2 -translate-x-1/2 text-slate-500 flex flex-col items-center gap-2"
            >
                <span className="text-xs uppercase tracking-widest">
                    Scroll to explore
                </span>
                <div className="w-6 h-10 border-2 border-slate-600 rounded-full flex justify-center p-1">
                    <motion.div
                        animate={{ y: [0, 12, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className="w-1.5 h-1.5 bg-cyan-500 rounded-full"
                    />
                </div>
            </motion.div>
        </section>
    );
};
